import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { postExamen, getEvaPorId } from '../services/api';
import fieldConfig, { EXAMENES_SOLO_CONCLUSION } from '../config/fieldConfig';
import { getTodayDate } from '../utils/getDate';
import { calcularIMC } from '../utils/imc';
import {sanitizePayload} from '../utils/sanitizePayload';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';

const CrearExamen = () => {
  const { evaluacionId, tipo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [parametrosSol, setParametrosSol] = useState(location.state?.parametros_sol || []);
  const [formData, setFormData] = useState({
    fecha_toma: getTodayDate(),
    fecha_disponibilidad: getTodayDate(),
  });
  const camposTablaVisual = [
    "ojo_der_lejos", "ojo_izq_lejos",
    "ojo_der_cerca", "ojo_izq_cerca"
  ];
  useEffect(() => {
    if (tipo === 'sangre' && parametrosSol.length === 0) {
      const recuperarParametros = async () => {
        try {
          const { data } = await getEvaPorId(evaluacionId);
          const relacionSangre = data.tipos_examen.find(te => te.codigo === 'sangre');
          if (relacionSangre?.parametros_sol) {
            setParametrosSol(relacionSangre.parametros_sol);
          }
        } catch (error) {
          console.error("Error al recuperar parámetros de la evaluación:", error);
        }
      };
      recuperarParametros();
    }
  }, [evaluacionId, tipo, parametrosSol]);
  useEffect(() => {
    if (tipo === 'antropometrico') {
      const peso = formData.peso;
      const talla = formData.talla;
      const nuevoIMC = calcularIMC(peso, talla);
      if (formData.imc !== nuevoIMC) {
        setFormData(prev => ({
          ...prev,
          imc: nuevoIMC
        }));
      }
    }
  }, [formData.peso, formData.talla, tipo, formData.imc]);

  const baseFields = fieldConfig[tipo] || [];
  const fields = tipo === 'sangre' && parametrosSol.length > 0
    ? baseFields.filter(f => parametrosSol.includes(f.name))
    : baseFields;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let parsedValue = value;
    if (type === "number") {
      parsedValue = value === "" ? null : Number(value);
    } else if (type === "select-one" || type === "text" || type === "textarea") {
      parsedValue = value.trim() === "" ? null : value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : parsedValue,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const confirmed = await confirmar(`Estás a punto de crear el examen "${tipo}"`, { textoAceptar: 'Crear' });
    if (!confirmed) return;

    try {
      const sanitized = sanitizePayload(formData, tipo);
      const payload = { ...sanitized };
      if (formData.estado_conclusion || formData.conclusion_txt) {
        payload.conclusion_examen = {
          estado_conclusion: formData.estado_conclusion || null,
          conclusion_txt: formData.conclusion_txt || null,
        };
      }
      if (EXAMENES_SOLO_CONCLUSION.includes(tipo)) {
        payload.comentarios = formData.conclusion_txt || null;
      }

      await postExamen(evaluacionId, tipo, payload);
      toast.success("Examen creado exitosamente");
      navigate(`/evaluaciones/${evaluacionId}/examenes`);
    } catch (error) {
      console.error("Error creando examen:", error);
      toast.error("No se pudo crear el examen");
    }
  };


  return (
    <div className="content-wrapper">
      <h1>Crear Examen ({tipo})</h1>
      <form onSubmit={handleSubmit} className="container">
        <div className="form-group required">
          <label>Fecha de Toma<span className='required-text'>*Obligatorio</span></label>
          <input
            type="date"
            name="fecha_toma"
            value={formData.fecha_toma ?? ""}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="form-group">
          <label>Fecha Disponibilidad</label>
          <input
            type="date"
            name="fecha_disponibilidad"
            value={formData.fecha_disponibilidad ?? ""}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        {tipo === "audiometria" ? (
          <div className='form-group'>
            <label>Audiometría</label>
            <table className='audiometria-table'>
              <thead>
                <tr>
                  <th></th>
                  <th>500 Hz</th>
                  <th>1000 Hz</th>
                  <th>2000 Hz</th>
                  <th>3000 Hz</th>
                  <th>4000 Hz</th>
                  <th>6000 Hz</th>
                  <th>8000 Hz</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>OD</td>
                  {["od500", "od1000", "od2000", "od3000", "od4000", "od6000", "od8000"].map(name => (
                    <td key={name}>
                      <input
                        type="number"
                        name={name}
                        value={formData[name] ?? ""}
                        onChange={handleChange}
                        className='form-control'
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>OI</td>
                  {["oi500", "oi1000", "oi2000", "oi3000", "oi4000", "oi6000", "oi8000"].map(name => (
                    <td key={name}>
                      <input
                        type="number"
                        name={name}
                        value={formData[name] ?? ""}
                        onChange={handleChange}
                        className='form-control'
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : tipo === "vista" ? (
          <div className="form-group">
            <label>Agudeza Visual (Medidas 20/XX)</label>
            <table className="visual-table">
              <thead>
                <tr>
                  <th>Visión</th>
                  <th>Ojo Derecho (OD)</th>
                  <th>Ojo Izquierdo (OI)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Lejos</td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_der_lejos"
                        value={formData.ojo_der_lejos ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_izq_lejos"
                        value={formData.ojo_izq_lejos ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Cerca</td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_der_cerca"
                        value={formData.ojo_der_cerca ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_izq_cerca"
                        value={formData.ojo_izq_cerca ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            {fields
              .filter(f => !camposTablaVisual.includes(f.name))
              .map((f) => (
                <div key={f.name} className="form-group">
                  <label>{f.label}</label>
                  {f.type === "select" ? (
                    <select
                      name={f.name}
                      onChange={handleChange}
                      className="form-control"
                      value={formData[f.name] ?? ""}
                    >
                      <option value="">Seleccione...</option>
                      {f.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      name={f.name}
                      checked={!!formData[f.name]}
                      onChange={handleChange}
                    />
                  ) : (
                    <input
                      type={f.type}
                      name={f.name}
                      step={f.type === "number" ? "any" : undefined}
                      value={formData[f.name] ?? ""}
                      onChange={handleChange}
                      className="form-control"
                    />
                  )}
                </div>
              ))}
          </div>
        ) : (
          fields.map(f => (
            <div key={f.name} className="form-group">
              <label>{f.label}</label>
              {f.type === "select" ? (
                <select
                  name={f.name}
                  onChange={handleChange}
                  className="form-control"
                  value={formData[f.name] ?? ""}
                >
                  <option value="">Seleccione...</option>
                  {f.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : f.type === "checkbox" ? (
                <input
                  type="checkbox"
                  name={f.name}
                  checked={!!formData[f.name]}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type={f.type}
                  name={f.name}
                  step={f.type === "number" ? "any" : undefined}
                  value={formData[f.name] ?? ""}
                  onChange={handleChange}
                  className="form-control"
                  readOnly={f.name === 'imc'}
                />
              )}
            </div>
          ))
        )}

        <div className="form-group">
          <label>Estado de Conclusión</label>
          <select
            name="estado_conclusion"
            value={formData.estado_conclusion ?? ""}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Seleccione...</option>
            <option value="normal">Normal</option>
            <option value="alterado">Alterado</option>
            <option value="no aplica">No Aplica</option>
          </select>
        </div>

        <div className="form-group">
          <label>Texto de Conclusión</label>
          <textarea
            name="conclusion_txt"
            value={formData.conclusion_txt ?? ""}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        <button type="submit" className="btn-submit">Guardar Examen</button>
      </form>
    </div>
  );
};

export default CrearExamen;